import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { CircleCheck, KeyRound } from 'lucide-react';

import { useAuth } from '@/features/auth/useAuth';
import { replaceSession } from '@/lib/api';
import {
  describeChangePasswordFailure,
  EMPTY_CHANGE_PASSWORD_FORM,
  validateChangePassword,
  type ChangePasswordErrors,
  type ChangePasswordField,
  type ChangePasswordForm,
} from './changePassword';

/**
 * Change your own password (owner, 2026-09-23: "all the employees should be
 * able to change their account password"). Reached from the key beside the
 * name in the top bar; open to every signed-in employee, no permission.
 *
 * The server signs out every OTHER device on the account and hands this one a
 * fresh session in the same answer, so saving never bounces the person who just
 * proved their old password back to the sign-in page.
 *
 * `onChanged` runs after a change is saved. The forced change (a password the
 * seller set or reset, EclipseGG platform 2026-10-19) uses it to re-read
 * `/auth/me` — the same save cleared the flag — and let the app open up.
 */
export function ChangePasswordPage({ onChanged }: { onChanged?: () => void } = {}) {
  const { employee } = useAuth();
  const [form, setForm] = useState<ChangePasswordForm>(EMPTY_CHANGE_PASSWORD_FORM);
  const [showErrors, setShowErrors] = useState(false);
  const [serverFailure, setServerFailure] = useState<{
    field?: ChangePasswordField;
    key: string;
  } | null>(null);
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);

  const errors = showErrors ? validateChangePassword(form) : {};
  if (serverFailure?.field && !errors[serverFailure.field]) {
    errors[serverFailure.field] = serverFailure.key;
  }

  function update(field: ChangePasswordField, value: string): void {
    setForm((previous) => ({ ...previous, [field]: value }));
    // A refusal is about what was sent; typing again clears it.
    setServerFailure(null);
    setDone(false);
  }

  async function handleSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    setShowErrors(true);
    setServerFailure(null);
    if (Object.keys(validateChangePassword(form)).length > 0) return;

    setSaving(true);
    try {
      // Not a plain POST: the answer replaces this tab's session, and nothing
      // else in the tab may refresh with the retiring cookie meanwhile.
      await replaceSession('/auth/password', {
        currentPassword: form.currentPassword,
        newPassword: form.newPassword,
      });
      setForm(EMPTY_CHANGE_PASSWORD_FORM);
      setShowErrors(false);
      setDone(true);
      onChanged?.();
    } catch (caught) {
      setServerFailure(describeChangePasswordFailure(caught));
    } finally {
      setSaving(false);
    }
  }

  return (
    <ChangePasswordView
      name={employee?.name ?? ''}
      form={form}
      errors={errors}
      formError={serverFailure && !serverFailure.field ? serverFailure.key : null}
      saving={saving}
      done={done}
      onChange={update}
      onSubmit={(event) => void handleSubmit(event)}
    />
  );
}

export interface ChangePasswordViewProps {
  name: string;
  form: ChangePasswordForm;
  /** Field -> i18n key. */
  errors: ChangePasswordErrors;
  /** i18n key of a refusal that belongs to no one field. */
  formError: string | null;
  saving: boolean;
  done: boolean;
  onChange: (field: ChangePasswordField, value: string) => void;
  onSubmit: (event: FormEvent) => void;
}

/** The form itself, stateless so its states can be rendered in tests. */
export function ChangePasswordView({
  name,
  form,
  errors,
  formError,
  saving,
  done,
  onChange,
  onSubmit,
}: ChangePasswordViewProps) {
  const { t } = useTranslation();
  const [reveal, setReveal] = useState(false);
  const inputType = reveal ? 'text' : 'password';

  const field = (
    key: ChangePasswordField,
    label: string,
    autoComplete: 'current-password' | 'new-password',
    hint?: string,
  ) => {
    const error = errors[key];
    const describedBy = error ? `account-password-${key}-error` : undefined;
    return (
      <label className="mb-4 block">
        <span className="mb-1 block text-sm text-content-secondary">{label}</span>
        <input
          type={inputType}
          name={key}
          value={form[key]}
          onChange={(event) => onChange(key, event.target.value)}
          autoComplete={autoComplete}
          aria-invalid={error ? true : undefined}
          aria-describedby={describedBy}
          className={`w-full rounded-control border bg-input px-3 py-2 text-content outline-none focus:border-primary ${
            error ? 'border-danger' : 'border-subtle'
          }`}
        />
        {error ? (
          <span id={describedBy} className="mt-1 block text-xs text-danger">
            {t(error)}
          </span>
        ) : hint ? (
          <span className="mt-1 block text-xs text-content-muted">{hint}</span>
        ) : null}
      </label>
    );
  };

  return (
    <div className="flex h-full flex-col overflow-auto">
      <header className="flex flex-wrap items-center gap-3 p-4">
        <KeyRound className="h-5 w-5 text-primary" aria-hidden />
        <h1 className="text-xl font-semibold text-content">{t('account.password.title')}</h1>
      </header>

      <div className="px-4 pb-6">
        <form
          onSubmit={onSubmit}
          noValidate
          className="w-full max-w-md rounded-card border border-subtle bg-raised p-5 sm:p-6"
        >
          <p className="mb-5 text-sm text-content-secondary">
            {t('account.password.intro', { name })}
          </p>

          {field('currentPassword', t('account.password.current'), 'current-password')}
          {field(
            'newPassword',
            t('account.password.new'),
            'new-password',
            t('account.password.newHint'),
          )}
          {field('confirmPassword', t('account.password.confirm'), 'new-password')}

          <label className="mb-5 flex items-center gap-2 text-sm text-content-secondary">
            <input
              type="checkbox"
              checked={reveal}
              onChange={(event) => setReveal(event.target.checked)}
            />
            {t('account.password.show')}
          </label>

          {formError ? (
            <p role="alert" className="mb-4 text-sm text-danger">
              {t(formError)}
            </p>
          ) : null}

          {done ? (
            <p
              role="status"
              className="mb-4 flex items-start gap-2 rounded-control border border-subtle bg-hover px-3 py-2 text-sm text-content"
            >
              <CircleCheck className="mt-0.5 h-4 w-4 shrink-0 text-primary" aria-hidden />
              {t('account.password.done')}
            </p>
          ) : null}

          <button
            type="submit"
            disabled={saving}
            className="w-full rounded-full bg-primary px-4 py-2 font-medium text-primary-fg transition-colors hover:bg-primary-hover disabled:opacity-60"
          >
            {saving ? t('account.password.saving') : t('account.password.save')}
          </button>
        </form>
      </div>
    </div>
  );
}
